import _ from 'lodash';
import {
  getCursor,
  getRepoModalTableData,
  getRepoModalTableColumns,
  getActiveRepositories,
  destructureSelected,
  handleSelection
} from './repositories-modal.service';

const useOwnerRepositories = ({
  providerConfig,
  repositories,
  selected,
  selectedOrg,
  setSelected,
  isOnboarding,
  numOfFreeSlots,
  page,
  pageParams,
  term,
  unselectedForNewRepos,
  setUnselectedForNewRepos,
  isBitbucket
}) => {
  const selectedByOrg = _.get(selected, `[${selectedOrg}]`, {});
  const {
    isSelectAll,
    excluded,
    specificIds,
    totalReposByOrg,
    inactiveRepos,
    isFoundActiveRepos
  } = destructureSelected(selectedByOrg);
  const activeRepositories = getActiveRepositories(repositories);

  const toggleSelectAll = () => {
    setSelected({
      ...selected,
      [selectedOrg]: {
        total: totalReposByOrg,
        inactiveRepos,
        isFoundActiveRepos,
        selectAll: {
          isSelectAll: !isSelectAll,
          excluded: new Set()
        }
      }
    });
  };

  const toggleRepositorySelection = repositoryId => {
    const selectedRepo = handleSelection({
      repositoriesIds: [repositoryId],
      selectedByOrg,
      action: 'toggleRepo',
      term,
      cursor: getCursor(pageParams, page, isBitbucket),
      unselectedForNewRepos,
      setUnselectedForNewRepos
    });

    setSelected({
      ...selected,
      [selectedOrg]: {
        total: totalReposByOrg,
        inactiveRepos,
        isFoundActiveRepos,
        ...selectedRepo
      }
    });
  };

  const togglePageSelect = () => {
    if (isPageDisabled()) return;

    if (isPageSelected() || isPageIndeterminate()) {
      unselectAllInPage();
      return;
    }

    if (isSelectAll || numOfFreeSlots > 0) {
      selectAllInPage();
    }
  };

  const selectAllInPage = () => {
    const activeRepositoriesIds = activeRepositories.map(({ id }) => id);
    const selectedRepos = handleSelection({
      repositoriesIds: isSelectAll
        ? activeRepositoriesIds
        : activeRepositoriesIds.slice(0, numOfFreeSlots),
      selectedByOrg,
      action: 'selectAllInPage',
      term,
      cursor: getCursor(pageParams, page, isBitbucket),
      unselectedForNewRepos,
      setUnselectedForNewRepos
    });

    setSelected({
      ...selected,
      [selectedOrg]: {
        total: totalReposByOrg,
        inactiveRepos,
        isFoundActiveRepos,
        ...selectedRepos
      }
    });
  };

  const unselectAllInPage = () => {
    const selectedRepos = handleSelection({
      repositoriesIds: activeRepositories.map(({ id }) => id),
      selectedByOrg,
      action: 'unselectAllInPage',
      unselectedForNewRepos,
      setUnselectedForNewRepos
    });

    setSelected({
      ...selected,
      [selectedOrg]: {
        total: totalReposByOrg,
        inactiveRepos,
        isFoundActiveRepos,
        ...selectedRepos
      }
    });
  };

  const isRepoDisabled = repositoryId => {
    return !isSelectAll && !isRepoSelected(repositoryId) && numOfFreeSlots <= 0;
  };

  const isRepoSelected = repositoryId => {
    const isExcluded = excluded.has(repositoryId);
    const isSelected = specificIds.includes(repositoryId);

    return (isSelectAll && !isExcluded) || isSelected;
  };

  const isPageSelected = () => {
    const isNoReposExcluded = !_.some(
      activeRepositories.map(({ id }) => id),
      id => excluded.has(id)
    );

    const isAllReposSelected = _.every(
      activeRepositories.map(({ id }) => id),
      id => specificIds.includes(id)
    );

    return isSelectAll ? isNoReposExcluded : isAllReposSelected;
  };

  const isPageIndeterminate = () => {
    const isAllReposExcluded = _.every(
      activeRepositories.map(({ id }) => id),
      id => excluded.has(id)
    );

    const isSomeReposSelected = _.some(
      activeRepositories.map(({ id }) => id),
      id => specificIds.includes(id)
    );

    return isSelectAll ? !isAllReposExcluded : isSomeReposSelected;
  };

  const isPageDisabled = () =>
    _.every(
      repositories,
      ({ isInactive, isDiscovered }) => isInactive || isDiscovered
    );

  const numOfSelectedInPage = () => {
    const excludedInPage = activeRepositories.filter(({ id }) =>
      excluded.has(id)
    );

    const selectedInPage = activeRepositories.filter(({ id }) =>
      specificIds.includes(id)
    );

    return isSelectAll
      ? activeRepositories.length - excludedInPage.length
      : selectedInPage.length;
  };

  const columns = getRepoModalTableColumns({
    numOfActiveRepositories: activeRepositories.length
  });

  const data = getRepoModalTableData({
    providerConfig,
    numOfFreeSlots,
    repositories,
    isOnboarding,
    isRepoDisabled,
    isRepoSelected,
    toggleRepositorySelection
  });

  return {
    columns,
    data,
    toggleSelectAll,
    togglePageSelect,
    unselectAllInPage,
    selectAllInPage,
    isSelectAll,
    isPageDisabled,
    isPageSelected,
    isPageIndeterminate,
    numOfSelectedInPage,
    totalReposByOrg,
    isFoundActiveRepos
  };
};

export default useOwnerRepositories;
